package com.narbase.letsgo.web.views.linkshare

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.drawable.Color
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.dto.linkshare.LinkShareRewardSettingDto
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.verticalFiller
import com.narbase.letsgo.web.utils.views.*
import com.narbase.letsgo.web.utils.views.customViews.CustomCheckBox
import com.narbase.letsgo.web.utils.views.customViews.customCheckBox
import kotlin.js.Date

class UpsertLinkShareRewardSettingDialog(private val onUpdated: () -> Unit) {

    private val viewModel = LinkSharesManagementViewModel()
    private val popUp = popUpDialog { viewModel.upsertUiState.value = null }

    private var dtoToEdit: LinkShareRewardSettingDto? = null

    private var nameInput: TextInput? = null
    private var descriptionInput: TextArea? = null
    private var bonusAmountInput: TextInput? = null
    private var maxBeneficiariesInput: TextInput? = null
    private var perLinkLimitInput: TextInput? = null
    private var expiresOnInput: TextInput? = null
    private var isActiveCheckBox: CustomCheckBox? = null
    private var uniqueUsersCheckBox: CustomCheckBox? = null
    private var errorTextView: TextView? = null

    fun add() {
        dtoToEdit = null
        showDialog()
    }

    fun edit(dto: LinkShareRewardSettingDto) {
        dtoToEdit = dto
        showDialog()
    }

    private fun showDialog() {
        theme.showDialog(popUp) { dialogBuilder ->
            verticalLayout {
                style {
                    height = wrapContent
                    minWidth = 700.px
                    width = matchParent
                    backgroundColor = Color.white
                    borderRadius = 8.px
                    padding = 8.px
                }

                theme.bigTextView(this) {
                    text = if (dtoToEdit == null) "Add reward".localized() else "Edit reward".localized()
                }
                verticalFiller(12)

                nameInput = theme.labeledTextInput(this, "Name".localized()) { text = dtoToEdit?.name ?: "" }
                descriptionInput = theme.labeledTextArea(this, "Description".localized()) {
                    text = dtoToEdit?.description ?: ""
                }

                bonusAmountInput = theme.labeledTextInput(this, "Bonus amount".localized()) {
                    text = dtoToEdit?.bonusAmount?.toString() ?: ""
                }

                maxBeneficiariesInput =
                    theme.labeledTextInput(this, "Max rewarded rides overall (optional)".localized()) {
                        text = dtoToEdit?.maxBeneficiaries?.toString() ?: ""
                    }
                perLinkLimitInput = theme.labeledTextInput(this, "Per-link rewarded rides limit (optional)".localized()) {
                    text = dtoToEdit?.perLinkLimit?.toString() ?: ""
                }
                expiresOnInput = theme.labeledTextInput(this, "Expiration date yyyy-MM-dd (optional)".localized()) {
                    text = dtoToEdit?.expiresOn?.let { formatDate(it) } ?: ""
                }

                isActiveCheckBox = customCheckBox("Active".localized(), isSelected = dtoToEdit?.isActive ?: true)
                uniqueUsersCheckBox =
                    customCheckBox("Unique users only".localized(), isSelected = dtoToEdit?.uniqueUsersOnly ?: false)

                errorTextView = textView {
                    style {
                        marginTop = 8.px
                        fontSize = 14.px
                        color = AppColors.redLight
                    }
                    isVisible = false
                }

                dialogBuilder.bottomBar = {
                    horizontalLayout {
                        style {
                            width = matchParent
                            justifyContent = JustifyContent.End
                        }
                        theme.unimportantButton(this) {
                            text = "Dismiss".localized()
                            onClick = { popUp.dismissDialog() }
                        }
                        horizontalFiller(16)
                        val saveButton = theme.mainButton(this) {
                            text = "Save".localized()
                            onClick = { onSaveClicked() }
                        }
                        viewModel.upsertUiState.clearObservers()
                        saveButton.withLoadingAndError(viewModel.upsertUiState,
                            onRetryClicked = { onSaveClicked() },
                            onLoaded = {
                                popUp.dismissDialog()
                                onUpdated()
                            })
                    }
                }
            }
        }
    }

    private fun onSaveClicked() {
        val name = nameInput?.text?.trim() ?: ""
        val bonusAmount = bonusAmountInput?.text?.toDoubleOrNull()
        if (name.isBlank() || bonusAmount == null || bonusAmount <= 0.0) {
            showError("Please provide a name and a valid bonus amount".localized())
            return
        }
        val setting = LinkShareRewardSettingDto(
            id = dtoToEdit?.id,
            name = name,
            description = descriptionInput?.text?.ifBlank { null },
            isActive = isActiveCheckBox?.isSelected ?: true,
            uniqueUsersOnly = uniqueUsersCheckBox?.isSelected ?: false,
            expiresOn = parseDate(expiresOnInput?.text),
            bonusAmount = bonusAmount,
            zoneId = dtoToEdit?.zoneId,
            maxBeneficiaries = maxBeneficiariesInput?.text?.toIntOrNull(),
            perLinkLimit = perLinkLimitInput?.text?.toIntOrNull(),
            actualBeneficiariesCount = dtoToEdit?.actualBeneficiariesCount ?: 0,
        )
        viewModel.upsertSetting(setting) {}
    }

    private fun showError(message: String) {
        errorTextView?.text = message
        errorTextView?.isVisible = true
    }

    private fun parseDate(text: String?): Double? {
        if (text.isNullOrBlank()) return null
        val millis = Date(text).getTime()
        return if (millis.isNaN()) null else millis
    }

    private fun formatDate(millis: Double): String {
        val d = Date(millis)
        val month = (d.getMonth() + 1).toString().padStart(2, '0')
        val day = d.getDate().toString().padStart(2, '0')
        return "${d.getFullYear()}-$month-$day"
    }
}
